import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useArealplan, useDispensasjoner, useCreateDispensasjon, dispensasjonInputSchema } from '@origo/planregister';
import { Loader, Notice } from '@origo/origo-ui';
import { useToast } from '@origo/origo-components';
import DispensationsPage, { type NewDispensationState } from './DispensationsPage';
import type { DispensationFieldErrors } from './components/DispensationForm';
import {
    toDispensationGroups,
    emptyDispensasjonForm,
    toDispensasjonInput,
    isValidSaksnummer,
} from '../../mappers/toDispensations';
import type { DispensationGroup, DispensasjonFormValues } from '../../types';

type ParseResult = ReturnType<typeof dispensasjonInputSchema.safeParse>;
type ParseError = Extract<ParseResult, { success: false }>['error'];

/** Statiske schema-meldinger (nb) mappet til i18n-nøkler, slik at feilteksten
 *  følger appens språkvalg i stedet for å alltid vises på norsk. */
const ERROR_MESSAGE_KEYS: Record<string, string> = {
    'Vedtaksdato er påkrevd': 'dispensations.errors.vedtaksdatoRequired',
    'Velg en gyldig dispensasjonstype': 'dispensations.errors.invalidType',
    'Velg et gyldig lovverk': 'dispensations.errors.invalidFra',
    'Varighet fra er påkrevd når dispensasjonen ikke er varig': 'dispensations.errors.varighetFraRequired',
    'Varighet til er påkrevd når dispensasjonen ikke er varig': 'dispensations.errors.varighetTilRequired',
    'Varighet til må være etter varighet fra': 'dispensations.errors.varighetOrder',
    'nasjonalArealplanId er påkrevd': 'dispensations.errors.napIdRequired',
};

function translateSchemaMessage(message: string, t: TFunction): string {
    const key = ERROR_MESSAGE_KEYS[message];
    return key ? t(key, { defaultValue: message }) : message;
}

/**
 * Feltnære feil fra pakkens zod-schema (jf. DocumentsContainer). Issues på felt
 * som ikke finnes i skjemaet (f.eks. `nasjonalArealplanId`) samles opp separat
 * i stedet for å forsvinne stille — de vises via toast slik at submit aldri
 * blir en no-op uten synlig forklaring.
 */
function collectFieldErrors(
    error: ParseError,
    t: TFunction
): { fieldErrors: DispensationFieldErrors; unmapped: string[] } {
    const knownKeys = new Set(Object.keys(emptyDispensasjonForm()));
    const fieldErrors: DispensationFieldErrors = {};
    const unmapped: string[] = [];
    for (const issue of error.issues) {
        const path = issue.path[0];
        const message = translateSchemaMessage(issue.message, t);
        if (typeof path === 'string' && knownKeys.has(path)) {
            const key = path as keyof DispensasjonFormValues;
            if (!fieldErrors[key]) fieldErrors[key] = message;
        } else {
            unmapped.push(message);
        }
    }
    return { fieldErrors, unmapped };
}

/** Felt som valideres på tvers av hverandre — en feilmelding på det ene kan
 *  forsvinne som følge av en endring på det andre, så de ryddes sammen. */
const CROSS_VALIDATED_FIELDS = new Set<keyof DispensasjonFormValues>(['varighetFra', 'varighetTil']);

interface DispensationsContainerProps {
    napId: string;
}

const DispensationsContainer: React.FC<DispensationsContainerProps> = ({ napId }) => {
    const { t } = useTranslation();
    const toast = useToast();
    const { data, isLoading, isError } = useDispensasjoner(napId);
    const create = useCreateDispensasjon(napId);
    // Samme query som PlanregisterView (header-tittel) — React Query dedupliserer på
    // query-key, så dette utløser ikke et ekstra kall.
    const { data: plan } = useArealplan(napId);

    const groups = useMemo(
        () => (data ? toDispensationGroups(data, t) : []),
        [data, t]
    );

    const [openGroups, setOpenGroups] = useState<Set<DispensationGroup['key']>>(new Set(['approved']));
    const onToggleGroup = useCallback((key: DispensationGroup['key']) => {
        setOpenGroups(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const [newOpen, setNewOpen] = useState(false);
    const [newDraft, setNewDraft] = useState<DispensasjonFormValues>(() => emptyDispensasjonForm());
    const [newErrors, setNewErrors] = useState<DispensationFieldErrors>({});

    const openNew = useCallback(() => {
        setNewDraft(emptyDispensasjonForm());
        setNewErrors({});
        setNewOpen(true);
    }, []);

    const closeNew = useCallback(() => {
        // Unngå at en lagring som fullføres etter lukk/gjenåpning overskriver et nytt utkast.
        if (!create.isPending) setNewOpen(false);
    }, [create.isPending]);

    const changeNew = useCallback(
        <K extends keyof DispensasjonFormValues>(field: K, value: DispensasjonFormValues[K]) => {
            setNewDraft(d => ({ ...d, [field]: value }));
            setNewErrors(e => {
                if (CROSS_VALIDATED_FIELDS.has(field as keyof DispensasjonFormValues)) {
                    if (!e.varighetFra && !e.varighetTil) return e;
                    return { ...e, varighetFra: undefined, varighetTil: undefined };
                }
                return e[field] ? { ...e, [field]: undefined } : e;
            });
        },
        []
    );

    const saveNew = useCallback(async () => {
        const saksnummerError = isValidSaksnummer(newDraft.saksnummer)
            ? undefined
            : t('dispensations.errors.invalidSaksnummer');

        const input = toDispensasjonInput(newDraft, napId, plan);
        const parsed = dispensasjonInputSchema.safeParse(input);
        if (!parsed.success || saksnummerError) {
            const { fieldErrors, unmapped } = parsed.success
                ? { fieldErrors: {} as DispensationFieldErrors, unmapped: [] as string[] }
                : collectFieldErrors(parsed.error, t);
            setNewErrors({ ...fieldErrors, ...(saksnummerError && { saksnummer: saksnummerError }) });
            if (unmapped.length > 0) toast.error(unmapped[0]);
            return;
        }
        setNewErrors({});

        // `parsed.data` er den trimmede/validerte verdien av feltene schemaet dekker.
        // `dispensasjonId` (satt av toDispensasjonInput) valideres ikke av schemaet
        // og må derfor bevares fra `input`.
        const payload = { ...input, ...parsed.data };
        const created = await toast.execute(() => create.mutateAsync(payload), {
            success: t('dispensations.created'),
        });
        if (created) {
            setNewOpen(false);
            setNewDraft(emptyDispensasjonForm());
        }
    }, [newDraft, napId, plan, toast, create, t]);

    if (isLoading) return <Loader label={t('common.loading')} />;
    if (isError) return <Notice variant="danger">{t('common.errorLoading')}</Notice>;

    const newDispensation: NewDispensationState = {
        open: newOpen,
        values: newDraft,
        fieldErrors: newErrors,
        isSaving: create.isPending,
    };

    return (
        <DispensationsPage
            groups={groups}
            openGroups={openGroups}
            onToggleGroup={onToggleGroup}
            newDispensation={newDispensation}
            onNewOpen={openNew}
            onNewClose={closeNew}
            onNewChange={changeNew}
            onNewSave={saveNew}
        />
    );
};

export default DispensationsContainer;
